import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PEWTER_CSS, PewterTimeField } from "@majourneys/booking/pickers";
import { defaultStopTime, resolveStopTimeLabel } from "./trip-date";

import { Icon } from "@majourneys/icons";
interface TripStopTimeEditorProps {
  readonly stopIndex: number;
  readonly startTime: string | undefined;
  readonly endTime: string | undefined;
  readonly onSave: (
    startTime: string | undefined,
    endTime: string | undefined,
  ) => void;
  /**
   * Controlled open state, for when something else opens the editor (the
   * stop's ⋯ menu). Left out, the pill toggles it on its own.
   */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  /** No pill: the time shows on the stop's card and the ⋯ menu opens this. */
  readonly hideTrigger?: boolean;
}

/**
 * Clickable time pill that opens an inline editor with two pewter time
 * fields (the site never shows a browser-native picker). Save commits,
 * Escape / outside click cancels. The "Reset" button clears stored times
 * so the stop falls back to its deterministic default slot.
 */
export function TripStopTimeEditor({
  hideTrigger = false,
  stopIndex,
  startTime,
  endTime,
  onSave,
  open: openProp,
  onOpenChange,
}: TripStopTimeEditorProps) {
  const { t } = useTranslation();
  const startLabelId = useId();
  const endLabelId = useId();
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (next: boolean | ((o: boolean) => boolean)) => {
    const value = typeof next === "function" ? next(open) : next;
    if (openProp === undefined) setOpenState(value);
    onOpenChange?.(value);
  };
  const wrapRef = useRef<HTMLDivElement>(null);
  const defaults = defaultStopTime(stopIndex);
  const [draftStart, setDraftStart] = useState<string>(
    startTime ?? defaults.startTime,
  );
  const [draftEnd, setDraftEnd] = useState<string>(
    endTime ?? defaults.endTime,
  );

  // When the stored times change externally (eg. reset) or the
  // popover is re-opened, sync the draft values.
  useEffect(() => {
    if (open) {
      setDraftStart(startTime ?? defaults.startTime);
      setDraftEnd(endTime ?? defaults.endTime);
    }
  }, [open, startTime, endTime, defaults.startTime, defaults.endTime]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapRef.current?.contains(target)) return;
      // The pewter time panel is portalled to <body>: a press on it (or on
      // its phone backdrop) is still inside this editor.
      if (target instanceof Element && target.closest("[data-mj-pw-popover]")) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const label = resolveStopTimeLabel({ startTime, endTime }, stopIndex, t);
  const hasCustomTime = !!startTime || !!endTime;

  const commit = (event?: React.FormEvent) => {
    event?.preventDefault();
    // Basic sanity: if the user cleared both, treat as reset.
    if (!draftStart && !draftEnd) {
      onSave(undefined, undefined);
    } else {
      onSave(draftStart || undefined, draftEnd || undefined);
    }
    setOpen(false);
  };

  const reset = () => {
    onSave(undefined, undefined);
    setOpen(false);
  };

  return (
    <div className="mj-trip-tstop-time" ref={wrapRef}>
      {hideTrigger ? null : (
      <button
        type="button"
        className={`mj-trip-tstop__time${hasCustomTime ? " is-custom" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={t("trip.time.edit_aria", { label })}
      >
        <Icon name="ri-time-line" aria-hidden="true" />
        <span>{label}</span>
        <Icon name="ri-pencil-line" className="mj-trip-tstop__time-edit-hint" aria-hidden="true" />
      </button>
      )}
      {open && (
        <form className="mj-trip-tstop-time__popover" onSubmit={commit}>
          <div className="mj-trip-tstop-time__header">
            <Icon name="ri-time-line" aria-hidden="true" />
            <span>{t("trip.time.edit")}</span>
          </div>
          {/* The pewter field's focus and pressed states. */}
          <style>{PEWTER_CSS}</style>
          <div className="mj-trip-tstop-time__inputs">
            <div className="mj-trip-tstop-time__field">
              <span id={startLabelId}>{t("trip.time.start")}</span>
              <PewterTimeField
                labelId={startLabelId}
                title={t("trip.time.start")}
                value={draftStart}
                onChange={setDraftStart}
                placeholder="--:--"
              />
            </div>
            <span className="mj-trip-tstop-time__sep" aria-hidden="true">
              –
            </span>
            <div className="mj-trip-tstop-time__field">
              <span id={endLabelId}>{t("trip.time.end")}</span>
              <PewterTimeField
                labelId={endLabelId}
                title={t("trip.time.end")}
                value={draftEnd}
                onChange={setDraftEnd}
                placeholder="--:--"
              />
            </div>
          </div>
          <div className="mj-trip-tstop-time__footer">
            {hasCustomTime && (
              <button
                type="button"
                className="mj-trip-tstop-time__reset"
                onClick={reset}
              >
                {t("trip.time.reset")}
              </button>
            )}
            <button
              type="button"
              className="mj-trip-tstop-time__cancel"
              onClick={() => setOpen(false)}
            >
              {t("trip.cancel")}
            </button>
            <button type="submit" className="mj-trip-tstop-time__save">
              <Icon name="ri-check-line" aria-hidden="true" />
              {t("trip.time.save")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
